import React, { useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Paper, Stack, Typography, useTheme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { CheckCircleOutline } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../contexts/AuthContext';
import { useAttentionSummary } from '../../hooks/useAttentionSummary';
import { AttentionCategory, AttentionSeverity } from '../../types/notification';

interface CategoryGroup {
  category: AttentionCategory;
  count: number;
  severity: AttentionSeverity;
  linkPath: string;
}

const severityRank: Record<AttentionSeverity, number> = {
  OVERDUE: 2,
  WARNING: 1,
  INFO: 0,
};

const severityTone = (severity: AttentionSeverity): 'error' | 'warning' | 'info' => {
  switch (severity) {
    case 'OVERDUE':
      return 'error';
    case 'WARNING':
      return 'warning';
    default:
      return 'info';
  }
};

export const AttentionItemsStrip: React.FC = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { user } = useAuth();
  const companyId = user?.company?.id;
  const { data } = useAttentionSummary(companyId);

  const groups = useMemo<CategoryGroup[]>(() => {
    if (!data) return [];
    const byCategory = new Map<AttentionCategory, CategoryGroup>();
    data.items.forEach((item) => {
      const existing = byCategory.get(item.category);
      if (!existing) {
        byCategory.set(item.category, {
          category: item.category,
          count: 1,
          severity: item.severity,
          linkPath: item.linkPath,
        });
        return;
      }
      existing.count += 1;
      if (severityRank[item.severity] > severityRank[existing.severity]) {
        existing.severity = item.severity;
      }
    });
    return Array.from(byCategory.values()).sort(
      (a, b) => severityRank[b.severity] - severityRank[a.severity]
    );
  }, [data]);

  if (!data) {
    return null;
  }

  if (groups.length === 0) {
    return (
      <Paper
        variant="outlined"
        sx={{
          mb: { xs: 1, md: 1.5 },
          px: 2,
          py: 1,
          borderRadius: 1.75,
          borderColor: alpha(theme.palette.success.main, 0.2),
          backgroundColor: alpha(theme.palette.success.main, 0.06),
          display: 'flex',
          alignItems: 'center',
          gap: 1,
        }}
      >
        <CheckCircleOutline fontSize="small" color="success" />
        <Typography variant="body2" fontWeight={600} color="success.dark">
          {t('notifications.empty', "You're all caught up")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Stack
      direction="row"
      spacing={1}
      sx={{ mb: { xs: 1, md: 1.5 }, flexWrap: 'wrap', rowGap: 1 }}
    >
      {groups.map((group) => {
        const tone = severityTone(group.severity);
        return (
          <Box
            key={group.category}
            component={RouterLink}
            to={group.linkPath}
            sx={{
              textDecoration: 'none',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              border: `1px solid ${alpha(theme.palette[tone].main, 0.3)}`,
              backgroundColor: alpha(theme.palette[tone].main, 0.08),
              transition: theme.transitions.create(['background-color', 'box-shadow'], {
                duration: theme.transitions.duration.shorter,
              }),
              '&:hover': {
                backgroundColor: alpha(theme.palette[tone].main, 0.14),
                boxShadow: theme.shadows[1],
              },
            }}
          >
            <Typography variant="body2" fontWeight={700} color={`${tone}.dark`}>
              {group.count}
            </Typography>
            <Typography variant="body2" color={`${tone}.dark`}>
              {t(`notifications.categories.${group.category}`, group.category)}
            </Typography>
          </Box>
        );
      })}
    </Stack>
  );
};
